import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import {
  getTableRowsPayload,
  type TableRowsPayload,
  type TableRowsPayloadRef,
} from "@/lib/table-rows-cache";
import { collectHydratedTableIds } from "@/lib/table-utils";

import type { HydratedTable, RowGroup, TableMetadata } from "./types";

export function useHydratedTables(tables: TableMetadata[], rowGroups: RowGroup[]) {
  const hydratedTableIds = useMemo(
    () => collectHydratedTableIds(tables, rowGroups),
    [tables, rowGroups]
  );

  const { data: refs } = useQuery(
    convexQuery(
      api.specPilot.extractedTables.getRowsPayloadRefsByIds,
      hydratedTableIds.length > 0 ? { tableIds: hydratedTableIds } : "skip"
    )
  );

  const [payloadByTableId, setPayloadByTableId] = useState<
    Map<Id<"specPilotExtractedTables">, TableRowsPayload>
  >(new Map());

  useEffect(() => {
    let cancelled = false;

    if (!refs) {
      setPayloadByTableId(new Map());
      return () => {
        cancelled = true;
      };
    }

    async function hydrate() {
      try {
        const entries = await Promise.all(
          (refs as TableRowsPayloadRef[]).map(async (ref) => {
            const payload = await getTableRowsPayload(ref);
            return [ref.tableId as Id<"specPilotExtractedTables">, payload] as const;
          })
        );
        if (!cancelled) {
          setPayloadByTableId(new Map(entries));
        }
      } catch (error) {
        console.error("Failed to hydrate table rows", error);
        if (!cancelled) {
          setPayloadByTableId(new Map());
        }
      }
    }

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [refs]);

  const tableMetadataById = useMemo(
    () => new Map(tables.map((table) => [table._id, table])),
    [tables]
  );

  const hydratedTableById = useMemo(() => {
    const hydrated = new Map<Id<"specPilotExtractedTables">, HydratedTable>();
    for (const tableId of hydratedTableIds) {
      const metadata = tableMetadataById.get(tableId);
      const payload = payloadByTableId.get(tableId);
      if (!metadata || !payload) continue;

      hydrated.set(tableId, {
        ...metadata,
        headers: payload.headers,
        rows: payload.rows,
      });
    }
    return hydrated;
  }, [hydratedTableIds, tableMetadataById, payloadByTableId]);

  const hydratedTables = useMemo(
    () => [...hydratedTableById.values()],
    [hydratedTableById]
  );

  return {
    hydratedTableById,
    hydratedTables,
  };
}
